import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  getUserRequestList,
  updateUserRequestStatus,
  UserRequest,
} from "../../apis/userRequestApis";

interface UserRequestState {
  requests: UserRequest[];
  loading: boolean;
  error: string | null;
}

const initialState: UserRequestState = {
  requests: [],
  loading: false,
  error: null,
};

/* ================================
   FETCH USER REQUESTS
================================ */

export const fetchUserRequests = createAsyncThunk<
  UserRequest[],
  void,
  { rejectValue: string }
>("userRequests/fetch", async (_, { rejectWithValue }) => {
  try {
    const res = await getUserRequestList();
    return res.data;
  } catch (err: any) {
    return rejectWithValue(
      err?.response?.data?.data || "Failed to fetch user requests",
    );
  }
});

/* ================================
   REFETCH USER REQUESTS
================================ */

export const refetchUserRequests = createAsyncThunk<
  UserRequest[],
  void,
  { rejectValue: string }
>("userRequests/refetch", async (_, { rejectWithValue }) => {
  try {
    const res = await getUserRequestList();
    return res.data;
  } catch (err: any) {
    return rejectWithValue(
      err?.response?.data?.data || "Failed to refetch user requests",
    );
  }
});

/* ================================
   UPDATE USER REQUEST STATUS
================================ */

export const updateUserRequest = createAsyncThunk<
  void,
  {
    userId: string;
    status: "accept" | "reject" | "delete" | "activate";
    rejectReason?: string;
  },
  { rejectValue: string }
>("userRequests/updateStatus", async (payload, { rejectWithValue }) => {
  try {
    await updateUserRequestStatus(payload.userId, {
      status: payload.status,
      rejectReason: payload.rejectReason,
    });
  } catch (err: any) {
    return rejectWithValue(
      err?.response?.data?.data || "Failed to update user request",
    );
  }
});

/* ================================
   SLICE
================================ */

const userRequestSlice = createSlice({
  name: "userRequests",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      /* ---------- fetch ---------- */
      .addCase(fetchUserRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.requests = action.payload;
      })
      .addCase(fetchUserRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
        toast.error(state.error);
      })

      /* ---------- refetch ---------- */
      .addCase(refetchUserRequests.pending, (state) => {
        state.loading = true;
      })
      .addCase(refetchUserRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.requests = action.payload;
      })
      .addCase(refetchUserRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
        toast.error(state.error);
      })

      /* ---------- update status ---------- */
      .addCase(updateUserRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserRequest.fulfilled, (state, action) => {
        state.loading = false;

        // 🎯 Success toast based on action
        const requestStatus = action.meta.arg.status;

        if (requestStatus === "delete") {
          toast.success("User deleted successfully");
        } else if (requestStatus === "activate") {
          toast.success("User activated successfully");
        } else if (requestStatus === "accept") {
          toast.success("User request accepted");
        } else if (requestStatus === "reject") {
          toast.success("User request rejected");
        }
      })
      .addCase(updateUserRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
        toast.error(state.error);
      });
  },
});

export default userRequestSlice.reducer;
